import DefaultBlock from "/scripts/default/block";
import gsap from "gsap";
import { watchPreferredMotion } from "/utils/helpers/watchPreferredMotion";
import {
	HemisphereLight,
	BoxBufferGeometry,
	DirectionalLight,
	Mesh,
	MeshLambertMaterial,
	PerspectiveCamera,
	Scene,
	WebGLRenderer,
} from "three";

export default class CoreAnimate extends DefaultBlock {
	init() {
		const selector = gsap.utils.selector(this.block);

		const DOM = {
			wrap: selector(".core-animate__flair-wrapper"),
			items: selector(".core-animate__item"),
			tabs: selector(".core-animate__tab"),
			controls: selector(".core-animate__tab--button"),
			flairs: selector(".core-animate__flair"),
			cssFlair: selector("#animate-anything-css"),
			reactFlair: selector("#animate-anything-react"),
			svgFlair: selector("#animate-anything-svg g"),
			webglFlair: selector("#animate-anything-webgl"),
		};
		this.DOM = DOM;

		this.timelines = [];

		this.previousIndex = 0;
		this.currentIndex = 0;
		this.controlIndex = 0;

		this.initCoreAnimate();
		this.webGLRefresh = this.webGLRefresh.bind(this);
	}

	initEvents() {
		window.addEventListener("resize", this.webGLRefresh);
	}

	setCoreAnimate() {
		this.DOM.flairs.forEach((flair) => {
			if (flair.classList.contains("core-animate__flair--active")) {
				gsap.set(flair, {
					autoAlpha: 1,
				});
			} else {
				gsap.set(flair, {
					autoAlpha: 0,
				});
			}
		});

		gsap.set([this.DOM.cssFlair, this.DOM.svgFlair, this.DOM.reactFlair], {
			transformOrigin: "50% 50%",
		});

		this.createWebGLShape();
	}

	createWebGLShape() {
		const container = this.DOM.wrap[0];

		// Scene
		this.scene = new Scene();

		// Camera
		const aspect = container.clientWidth / container.clientHeight;
		this.camera = new PerspectiveCamera(35, aspect, 0.1, 10);
		this.camera.position.set(0, 0, 6);

		// Cube
		const geometry = new BoxBufferGeometry(2, 2, 2);
		const material = new MeshLambertMaterial({ color: 0x0ae448 });
		this.cube = new Mesh(geometry, material);
		this.scene.add(this.cube);

		// Lights
		const light = new DirectionalLight(0xffffff);
		light.position.set(1, 1, 1).normalize();
		this.scene.add(light);
		this.scene.add(new HemisphereLight(0x606060, 0x404040));

		// Renderer
		this.renderer = new WebGLRenderer({ alpha: true });
		this.renderer.setSize(container.clientWidth, container.clientHeight);
		this.renderer.setPixelRatio(window.devicePixelRatio);

		// Init
		this.DOM.webglFlair[0].append(this.renderer.domElement);
	}

	webGLRefresh() {
		const container = this.DOM.wrap[0];

		this.camera.aspect = container.clientWidth / container.clientHeight;
		this.camera.updateProjectionMatrix();
		this.renderer.setSize(container.clientWidth, container.clientHeight);
	}

	playCoreAnimate() {
		const timelines = [];

		this.DOM.flairs.forEach((flair) => {
			const tl = gsap.timeline({
				repeat: -1,
				paused: !flair.classList.contains("core-animate__flair--active"),
			});

			timelines.push(tl);
		});

		this.timelines = timelines;

		watchPreferredMotion(() => {
			// CSS Animation
			timelines[0].to(this.DOM.cssFlair, {
				rotation: 360,
				duration: 10,
				ease: "none",
			});

			// React Animation
			timelines[1].to(this.DOM.reactFlair, {
				rotation: 360,
				duration: 10,
				ease: "none",
			});

			// SVG Animation
			timelines[2].to(this.DOM.svgFlair, {
				rotation: 360,
				duration: 10,
				ease: "none",
			});

			// WebGL Animation
			timelines[3].to(this.cube.rotation, {
				x: Math.PI * 2,
				y: Math.PI * 2,
				duration: 10,
				ease: "none",
				onUpdate: () => {
					this.renderer.render(this.scene, this.camera);
				},
			});
		});
	}

	handleNavigation() {
		this.DOM.controls.forEach((control) => {
			control.addEventListener("click", (e) => {
				this.disableButtons();

				// Set active index
				const index = e.target.dataset.index;
				this.currentIndex = index;

				// Handle transitions
				this.handleCodeState();
				this.transitionOut();
			});
		});
	}

	transitionOut() {
		const index = this.previousIndex;
		const items = this.DOM.items;
		const item = items[index];

		const flairs = this.DOM.flairs;
		const flair = flairs[index];

		const flairTimelines = this.timelines;
		const flairTimeline = flairTimelines[index];

		const tl = gsap.timeline({
			default: {
				ease: "power2.inOut",
			},
			onComplete: () => {
				flairTimeline.pause();

				this.transitionIn();
			},
		});

		tl.to(
			[item, flair],
			{
				y: 20,
				autoAlpha: 0,
				duration: 0.4,
			},
			0
		);
	}

	transitionIn() {
		const index = this.currentIndex;
		const items = this.DOM.items;
		const item = items[index];

		const flairs = this.DOM.flairs;
		const flair = flairs[index];

		const flairTimelines = this.timelines;
		const flairTimeline = flairTimelines[index];

		flairTimeline.play();

		const tl = gsap.timeline({
			onComplete: () => {
				this.enableButtons();
			},
		});

		tl.fromTo(
			[item, flair],
			{
				y: 20,
				autoAlpha: 0,
			},
			{
				y: 0,
				autoAlpha: 1,
				duration: 0.4,
			},
			0
		);
	}

	disableButtons() {
		this.DOM.controls.forEach((control) => {
			control.disabled = true;
		});
	}

	enableButtons() {
		this.previousIndex = this.currentIndex;

		this.DOM.controls.forEach((control) => {
			control.disabled = false;
		});
	}

	handleCodeState() {
		const index = this.currentIndex;
		const items = this.DOM.items;
		const tabs = this.DOM.tabs;
		const flairs = this.DOM.flairs;

		this.DOM.items.forEach((item) =>
			item.classList.remove("core-animate__item--active")
		);

		this.DOM.tabs.forEach((tab) =>
			tab.classList.remove("core-animate__tab--active")
		);

		this.DOM.flairs.forEach((flair) => {
			flair.classList.remove("core-animate__flair--active");
		});

		// Set active control and item
		items[index].classList.add("core-animate__item--active");
		tabs[index].classList.add("core-animate__tab--active");
		flairs[index].classList.add("core-animate__flair--active");
	}

	initCoreAnimate() {
		this.setCoreAnimate();
		this.playCoreAnimate();
		this.handleNavigation();
	}
}
